import { useGetMillwrightsQuery } from '@/apis/millwrights/millwrightsApi';
import {
  INITIAL_PAGE,
  LIST_PAGE_SIZE,
  millwrightsRiskOptions,
  millwrightsSortOptions,
  SPINNER_SIZE,
} from '@/constants';
import { usePagination, useSearch } from '@/hooks';
import { MillwrightsList } from '@/pages/Millwrights/components';
import {
  LoadingSpinner,
  Select,
  SelectOption,
  Stack,
} from '@data-products-and-ai/react-components';
import {
  useMillwrightsFilters,
  useMillwrightsSort,
} from '@/pages/Millwrights/hooks';
import { ChangeEvent } from 'react';

export const MillwrightsPage: React.FC = () => {
  const { debouncedQuery, query, handleSearch } = useSearch();
  const { currentPage, pageSize, setPage } = usePagination({
    initialPage: INITIAL_PAGE,
    initialPageSize: LIST_PAGE_SIZE,
  });
  const { sortOption, updateSortOption } = useMillwrightsSort(
    millwrightsSortOptions[0],
  );

  const { millwrightsFilters, updateFilters } = useMillwrightsFilters();

  const {
    data: millwrightsData,
    isLoading,
    isFetching,
  } = useGetMillwrightsQuery({
    q: debouncedQuery,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    sortDir: sortOption.sortDir,
    sortBy: sortOption.sortBy,
    riskMin: millwrightsFilters?.riskFactor.range?.[0],
    riskMax: millwrightsFilters?.riskFactor.range?.[1],
  });
  const millwrights = millwrightsData?.results || [];
  const totalItems = millwrightsData?.total ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleSortChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const sortOption = millwrightsSortOptions.find(
      (option) => option.value === event.target.value,
    );

    if (sortOption) {
      updateSortOption(sortOption);
      setPage(INITIAL_PAGE);
    }
  };

  const handleRiskFilterChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const riskOption = millwrightsRiskOptions.find(
      (option) => option.value === event.target.value,
    );

    if (riskOption) {
      updateFilters({ riskFactor: riskOption });
      setPage(INITIAL_PAGE);
    }
  };

  if (isLoading) {
    return (
      <div className="outages-page-spinner">
        <LoadingSpinner
          text="Loading millwrights..."
          size={SPINNER_SIZE}
          color="var(--color-primary)"
          align="center"
          tag="textdefault"
          inline
        />
      </div>
    );
  }

  return (
    <MillwrightsList
      hasSearch
      hasPagination
      clickable={false}
      list={millwrights}
      totalItems={totalItems}
      totalPages={totalPages}
      loading={isFetching}
      searchQuery={query}
      currentPage={currentPage}
      header="Active millwrights"
      sortComponent={
        <Stack direction="row" gap={16}>
          <Select defaultValue={sortOption.value} onChange={handleSortChange}>
            {millwrightsSortOptions.map((option) => (
              <SelectOption
                key={option.value}
                value={option.value}
                label={option.label}
              />
            ))}
          </Select>
          <Select
            defaultValue={millwrightsFilters.riskFactor.value}
            onChange={handleRiskFilterChange}
          >
            {millwrightsRiskOptions.map((option) => (
              <SelectOption
                key={option.value}
                value={option.value}
                label={option.label}
              />
            ))}
          </Select>
        </Stack>
      }
      onPageChange={handlePageChange}
      onSearchDone={handleSearch}
    />
  );
};
